"use client";

import { useState, useCallback, memo, useMemo, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { useRouter } from "next/navigation";
import {
  ArrowUp,
  MessageCircle,
  Eye,
  TrendingUp,
  Share2,
  ExternalLink,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ProductHoverPreview } from "./product-hover-preview";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth/auth-context";
import {getPlaceholderLogo, type PreviewImageDto, type CategorySimpleDto} from "@/lib/services/productService";
import { StatusTag, STATUS_TAG_CONFIG } from "./product-types";

// ============================================================================
// TYPE DEFINITIONS
// ============================================================================

/**
 * User/Founder information
 */
interface Founder {
  id: string;
  name: string;
  username: string;
  avatarUrl?: string;
}

/**
 * Product card data structure
 */
interface ProductCardPHProps {
  /** Product information */
  product: {
    id: string;
    slug: string;
    name: string;
    tagline: string;
    description?: string;
    logoUrl: string;
    websiteUrl?: string;
    previewImages?: PreviewImageDto[];
    screenshots?: string[]; // Deprecated: use previewImages instead
    highlights?: string[];
    voteCount: number;
    viewCount: number;
    commentCount: number;
    statusTags?: StatusTag[];
    categories: CategorySimpleDto[];
    founder: Founder;
    isVoted?: boolean;
    weekRank?: number;
    launchDate?: Date;
  };

  /** Display rank badge (1-3) */
  rank?: number;

  /** User authentication state */
  isAuthenticated?: boolean;

  /** Compact display mode */
  compact?: boolean;

  /** Show skeleton loading state */
  isLoading?: boolean;

  /** Enable Steam-style hover preview */
  enableHoverPreview?: boolean;

  /** Callback handlers */
  onVote?: (productId: string) => Promise<void>;
  onShare?: (productId: string) => void;

  /** Analytics tracking */
  onAnalyticsEvent?: (event: AnalyticsEvent) => void;

  /** Custom className */
  className?: string;
}

/**
 * Analytics event structure
 */
interface AnalyticsEvent {
  type: "vote" | "share" | "view" | "click";
  productId: string;
  metadata?: Record<string, unknown>;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Format large numbers with K/M suffixes
 * @example formatCount(1234) => "1.2K"
 */
const formatCount = (count: number): string => {
  if (count >= 1_000_000) {
    return `${(count / 1_000_000).toFixed(1)}M`;
  }
  if (count >= 1_000) {
    return `${(count / 1_000).toFixed(1)}K`;
  }
  return count.toLocaleString("ru-RU");
};

/**
 * Get initials from name for avatar fallback
 */
const getInitials = (name: string): string => {
  return name
    .split(" ")
    .slice(0, 2)
    .map((word) => word[0])
    .join("")
    .toUpperCase();
};

/**
 * Share product via Web Share API or fallback to clipboard
 */
const shareProduct = async (
  productName: string,
  productUrl: string
): Promise<boolean> => {
  const shareData = {
    title: `${productName} на Peek Product`,
    text: `Посмотрите на ${productName} на Peek Product!`,
    url: productUrl,
  };

  // Try native share first (mobile)
  if (navigator.share) {
    try {
      await navigator.share(shareData);
      return true;
    } catch (error) {
      if ((error as Error).name !== "AbortError") {
        console.error("Share failed:", error);
      }
    }
  }

  // Fallback to clipboard
  try {
    await navigator.clipboard.writeText(productUrl);
    toast.success("Ссылка скопирована в буфер обмена");
    return true;
  } catch (error) {
    console.error("Clipboard write failed:", error);
    toast.error("Не удалось скопировать ссылку");
    return false;
  }
};

// ============================================================================
// MAIN COMPONENT
// ============================================================================

/**
 * ProductCardPH - Enterprise-grade product card component for Peek Product
 *
 * Features:
 * - Optimized performance with React.memo and useCallback
 * - Full WCAG AA+ accessibility compliance
 * - Comprehensive error handling and user feedback
 * - Analytics event tracking
 * - Mobile-optimized with touch targets
 * - Skeleton loading states
 * - Share functionality
 * - Rank badges for top 3 products
 *
 * @example
 * ```tsx
 * <ProductCardPH
 *   product={productData}
 *   rank={1}
 *   isAuthenticated={true}
 *   onVote={handleVote}
 *   onAnalyticsEvent={trackEvent}
 * />
 * ```
 */
export const ProductCardPH = memo<ProductCardPHProps>(
  ({
    product,
    rank,
    isAuthenticated = false,
    compact = false,
    isLoading = false,
    enableHoverPreview = true,
    onVote,
    onShare,
    onAnalyticsEvent,
    className,
  }) => {
    // ========================================================================
    // AUTH CONTEXT & ROUTER
    // ========================================================================

    const { login } = useAuth();
    const router = useRouter();

    // ========================================================================
    // STATE MANAGEMENT
    // ========================================================================

    const [voted, setVoted] = useState(product.isVoted || false);
    const [voteCount, setVoteCount] = useState(product.voteCount);
    const [isVoting, setIsVoting] = useState(false);
    const [logoError, setLogoError] = useState(false);

    // Sync state with prop changes (when products are refreshed after voting)
    useEffect(() => {
      setVoted(product.isVoted || false);
      setVoteCount(product.voteCount);
    }, [product.isVoted, product.voteCount]);

    // ========================================================================
    // MEMOIZED VALUES
    // ========================================================================

    const productUrl = useMemo(
      () =>
        typeof window !== "undefined"
          ? `${window.location.origin}/products/${product.slug}`
          : `/products/${product.slug}`,
      [product.slug]
    );


    // ========================================================================
    // EVENT HANDLERS
    // ========================================================================

    /**
     * Handle vote action with optimistic updates and error rollback
     */
    const handleVote = useCallback(async () => {
      if (!isAuthenticated || isVoting || !onVote) {
        if (!isAuthenticated) {
          toast.error("Войдите, чтобы голосовать", {
            action: {
              label: "Войти",
              onClick: () => {
                login();
              },
            },
          });
        }
        return;
      }

      // Track analytics
      onAnalyticsEvent?.({
        type: "vote",
        productId: product.id,
        metadata: { voted: !voted },
      });

      setIsVoting(true);
      const previousVoted = voted;
      const previousCount = voteCount;

      // Optimistic update
      const newVoted = !voted;
      setVoted(newVoted);
      setVoteCount((prev) =>
        newVoted ? prev + 1 : prev - 1
      );

      try {
        await onVote(product.slug);

        // Success feedback
        if (newVoted) {
          toast.success("Голос учтён! 👍");
        } else {
          toast.success("Голос отменён");
        }
      } catch (error) {
        // Rollback on error
        setVoted(previousVoted);
        setVoteCount(previousCount);

        console.error("Vote failed:", error);
        toast.error("Не удалось проголосовать. Попробуйте снова.", {
          duration: 3000,
        });
      } finally {
        setIsVoting(false);
      }
    }, [
      isAuthenticated,
      isVoting,
      voted,
      voteCount,
      onVote,
      onAnalyticsEvent,
      product.id,
      login,
    ]);

    /**
     * Handle share action
     */
    const handleShare = useCallback(async () => {
      onAnalyticsEvent?.({
        type: "share",
        productId: product.id,
      });

      if (onShare) {
        onShare(product.id);
      } else {
        await shareProduct(product.name, productUrl);
      }
    }, [onShare, onAnalyticsEvent, product.id, product.name, productUrl]);

    /**
     * Track click analytics
     */
    const handleClick = useCallback(
      (target: string) => {
        onAnalyticsEvent?.({
          type: "click",
          productId: product.id,
          metadata: { target },
        });
      },
      [onAnalyticsEvent, product.id]
    );

    /**
     * Handle card click - navigate to product page
     */
    const handleCardClick = useCallback(
      (e: React.MouseEvent) => {
        // Don't navigate if clicking on interactive elements
        const target = e.target as HTMLElement;
        if (
          target.closest("button") ||
          target.closest("a") ||
          target.closest('[role="button"]')
        ) {
          return;
        }

        handleClick("card");
        router.push(`/products/${product.slug}`);
      },
      [router, product.id, handleClick]
    );

    // ========================================================================
    // SKELETON LOADING STATE
    // ========================================================================

    if (isLoading) {
      return (
        <div
          className={cn(
            "group relative bg-card border border-border rounded-2xl p-4 animate-pulse",
            className
          )}
          aria-busy="true"
          aria-label="Загрузка продукта"
        >
          <div className="flex items-start gap-4">
            {/* Logo skeleton */}
            <div className="w-20 h-20 rounded-xl bg-muted" />

            {/* Content skeleton */}
            <div className="flex-1 space-y-3">
              <div className="h-6 bg-muted rounded w-3/4" />
              <div className="h-4 bg-muted rounded w-full" />
              <div className="flex gap-2">
                <div className="h-6 bg-muted rounded w-20" />
                <div className="h-6 bg-muted rounded w-20" />
              </div>
            </div>

            {/* Vote button skeleton */}
            <div className="w-14 h-16 bg-muted rounded-xl" />
          </div>
        </div>
      );
    }

    // ========================================================================
    // RENDER
    // ========================================================================

    const hasHoverPreview =
      enableHoverPreview &&
      !compact &&
      (product.previewImages?.length || product.screenshots?.length || product.description || product.highlights?.length);

    const cardContent = (
      <article
        className={cn(
          "group relative bg-card border border-border rounded-2xl transition-colors duration-200",
          "hover:border-border-hover hover:bg-card-hover",
          "focus-within:ring-2 focus-within:ring-ring focus-within:ring-offset-2",
          "cursor-pointer",
          className
        )}
        aria-label={`${product.name}: ${product.tagline}`}
        onClick={handleCardClick}
      >
          <div className={cn("flex items-start gap-4 p-4", compact && "p-3")}>
            {/* ============================================================
                PRODUCT LOGO & RANK
                ============================================================ */}
            <Link
              href={`/products/${product.slug}`}
              className="flex-shrink-0 relative group/logo"
              onClick={() => handleClick("logo")}
              aria-label={`Открыть ${product.name}`}
            >
              <div
                className={cn(
                  "relative rounded-xl overflow-hidden",
                  compact ? "w-16 h-16" : "w-20 h-20"
                )}
              >
                <Image
                  src={logoError ? getPlaceholderLogo() : product.logoUrl}
                  alt={`Логотип ${product.name}`}
                  width={compact ? 64 : 80}
                  height={compact ? 64 : 80}
                  className={cn("object-contain rounded-3xl", compact ? "p-1.5" : "p-2")}
                  quality={90}
                  loading="lazy"
                  sizes={compact ? "64px" : "80px"}
                  onError={() => setLogoError(true)}
                />
              </div>
            </Link>

            {/* ============================================================
                CONTENT SECTION
                ============================================================ */}
            <div className="flex-1 min-w-0">
              {/* Product Name & Status Dots */}
              <div className={cn("mb-2", compact && "mb-1")}>
                <div className="flex items-center gap-2">
                  <Link
                    href={`/products/${product.slug}`}
                    className="group/title inline-block"
                    onClick={() => handleClick("title")}
                  >
                    <h3
                      className={cn(
                        "font-semibold text-foreground group-hover:text-primary transition-colors",
                        compact ? "text-base" : "text-lg"
                      )}
                    >
                      {product.name}
                    </h3>
                  </Link>

                  {/* Status Dots */}
                  {product.statusTags && product.statusTags.length > 0 && (
                    <div className="flex items-center gap-1.5" role="list" aria-label="Статусы продукта">
                      {product.statusTags.map((tag) => {
                        const config = STATUS_TAG_CONFIG[tag];
                        return (
                          <Tooltip key={tag}>
                            <TooltipTrigger asChild>
                              <span
                                className={cn(
                                  "w-2.5 h-2.5 rounded-full cursor-help",
                                  config.dotColor
                                )}
                                role="listitem"
                                aria-label={config.label}
                              />
                            </TooltipTrigger>
                            <TooltipContent side="top">
                              <p className="text-xs">{config.label}</p>
                            </TooltipContent>
                          </Tooltip>
                        );
                      })}
                    </div>
                  )}
                </div>

                <p
                  className={cn(
                    "text-foreground-secondary mt-0.5 line-clamp-2",
                    compact ? "text-xs" : "text-sm"
                  )}
                >
                  {product.tagline}
                </p>
              </div>

              {/* Categories */}
              {!compact && product.categories.length > 0 && (
                <div
                  className="flex flex-wrap gap-2 mb-3"
                  role="list"
                  aria-label="Категории"
                >
                  {product.categories.slice(0, 3).map((category) => (
                    <span
                      key={category.id}
                      className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground"
                      role="listitem"
                    >
                      {category.name}
                    </span>
                  ))}
                  {product.categories.length > 3 && (
                    <span className="text-xs px-2.5 py-1 rounded-full bg-muted text-muted-foreground">
                      +{product.categories.length - 3}
                    </span>
                  )}
                </div>
              )}

              {/* ============================================================
                  ENGAGEMENT METRICS & FOOTER
                  ============================================================ */}
              <div
                className={cn(
                  "flex items-center gap-4 text-foreground-tertiary",
                  compact ? "text-xs" : "text-sm"
                )}
              >
                {/* Comments */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Link
                      href={`/products/${product.slug}#comments`}
                      className="flex items-center gap-1.5 hover:text-foreground transition-colors"
                      onClick={() => handleClick("comments")}
                      aria-label={`${product.commentCount} комментариев`}
                    >
                      <MessageCircle className="h-4 w-4" aria-hidden="true" />
                      <span>{product.commentCount}</span>
                    </Link>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Комментарии</p>
                  </TooltipContent>
                </Tooltip>

                {/* Views */}
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div
                      className="flex items-center gap-1.5"
                      aria-label={`${formatCount(product.viewCount)} просмотров`}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                      <span>{formatCount(product.viewCount)}</span>
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top">
                    <p className="text-xs">Просмотры</p>
                  </TooltipContent>
                </Tooltip>

                {/* Action Buttons */}
                <div className="flex items-center gap-1 ml-auto">
                  {/* Share */}
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleShare}
                        className="h-8 w-8 p-0 rounded-full"
                        aria-label="Поделиться продуктом"
                      >
                        <Share2 className="h-4 w-4" aria-hidden="true" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">Поделиться</p>
                    </TooltipContent>
                  </Tooltip>

                  {/* External Link */}
                  {product.websiteUrl && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          asChild
                          className="h-8 w-8 p-0 rounded-full"
                        >
                          <a
                            href={product.websiteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleClick("external-link")}
                            aria-label="Открыть сайт продукта"
                          >
                            <ExternalLink
                              className="h-4 w-4"
                              aria-hidden="true"
                            />
                          </a>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        <p className="text-xs">Открыть сайт</p>
                      </TooltipContent>
                    </Tooltip>
                  )}
                </div>

                {/* Founder Info */}
                {!compact && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Link
                        href={`/u/${product.founder.username}`}
                        className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                        onClick={() => handleClick("founder")}
                        aria-label={`Автор: ${product.founder.name}`}
                      >
                        <Avatar className="h-6 w-6 ring-1 ring-border">
                          <AvatarImage
                            src={product.founder.avatarUrl}
                            alt={product.founder.name}
                          />
                          <AvatarFallback className="text-xs bg-muted">
                            {getInitials(product.founder.name)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-foreground-secondary hidden sm:inline truncate max-w-[200px]">
                          {product.founder.name}
                        </span>
                      </Link>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      <p className="text-xs">
                        Основатель: {product.founder.name}
                      </p>
                    </TooltipContent>
                  </Tooltip>
                )}
              </div>
            </div>

            {/* ============================================================
                UPVOTE BUTTON
                ============================================================ */}
            <div className="flex-shrink-0">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleVote}
                    disabled={isVoting}
                    className={cn(
                      "flex flex-col items-center gap-1 h-auto min-w-[56px] transition-colors duration-200",
                      "border border-border rounded-xl",
                      compact ? "py-1.5 px-2" : "py-2 px-3",
                      voted
                        ? "bg-primary border-primary text-primary-foreground hover:bg-primary-hover hover:text-primary-foreground"
                        : "hover:border-primary hover:text-primary",
                      isVoting && "opacity-50 cursor-wait"
                    )}
                    aria-label={
                      voted
                        ? `Убрать голос (${voteCount})`
                        : `Проголосовать (${voteCount})`
                    }
                    aria-pressed={voted}
                  >
                    <ArrowUp
                      className={cn(
                        "transition-transform duration-200",
                        compact ? "h-4 w-4" : "h-5 w-5",
                        voted && "scale-110",
                        isVoting && "animate-pulse"
                      )}
                      aria-hidden="true"
                    />
                    <span
                      className={cn(
                        "font-semibold tabular-nums",
                        compact ? "text-xs" : "text-sm"
                      )}
                    >
                      {formatCount(voteCount)}
                    </span>
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-[200px]">
                  {!isAuthenticated ? (
                    <p className="text-xs">Войдите, чтобы голосовать</p>
                  ) : (
                    <p className="text-xs">
                      {voted ? "Убрать голос" : "Проголосовать за продукт"}
                    </p>
                  )}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        </article>
    );

    return (
      <TooltipProvider delayDuration={300}>
        {hasHoverPreview ? (
          <HoverCard openDelay={400} closeDelay={100}>
            <HoverCardTrigger asChild>{cardContent}</HoverCardTrigger>
            <HoverCardContent
              side="right"
              align="start"
              sideOffset={8}
              className="p-0 border-0 shadow-none bg-transparent"
            >
              <ProductHoverPreview product={product} />
            </HoverCardContent>
          </HoverCard>
        ) : (
          cardContent
        )}
      </TooltipProvider>
    );
  }
);

ProductCardPH.displayName = "ProductCardPH";
